import { memo } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export interface NavigationItem {
  to: string;
  text: string;
  exact?: boolean;
  authenticated?: boolean;
}

export interface AdditionalButton {
  type: 'link' | 'button';
  text: string;
  className?: string;
  authenticated?: boolean;
  onClick?: () => void;
  to?: string;
}

export interface PageNavigationProps {
  navigationItems?: NavigationItem[];
  additionalButtons?: AdditionalButton[];
  authenticated?: boolean;
  className?: string;
}

/**
 * Reusable PageNavigation component
 */
const PageNavigation = memo<PageNavigationProps>(
  ({ navigationItems = [], additionalButtons = [], authenticated = false, className }) => {
    const { t } = useTranslation();
    // Filter navigation items based on authentication
    const visibleNavigationItems = navigationItems.filter((item) => !item.authenticated || authenticated);

    // Filter additional buttons based on authentication
    const visibleAdditionalButtons = additionalButtons.filter((button) => !button.authenticated || authenticated);

    return (
      <nav className={clsx('page__navigation', className)}>
        <ul className="page__navigation-list">
          {visibleNavigationItems.map((item, index) => (
            <li key={index} className="page__navigation-item">
              <NavLink className="page__navigation-link" exact={item.exact || false} to={item.to}>
                {t ? t(item.text) : item.text}
              </NavLink>
            </li>
          ))}
        </ul>

        {visibleAdditionalButtons.map((button, index) => {
          if (button.type === 'link') {
            return (
              <NavLink key={index} className={button.className || 'page__add-button'} to={button.to || '#'}>
                {t ? t(button.text) : button.text}
              </NavLink>
            );
          }

          return (
            <button key={index} className={button.className || 'page__exit-button'} onClick={button.onClick}>
              {t(button.text, button.text)}
            </button>
          );
        })}
      </nav>
    );
  },
);

PageNavigation.displayName = 'PageNavigation';

export default PageNavigation;
